import React, { useEffect, useState } from "react";
import axios from "axios";
import "./ContrastGame.css";

const lettersSet = ["A","B","C","D","E","F","H","K","N","P","R","S","T","U","V","Z"];

// ================= CONSTANTS (PYTHON MATCH) =================
const STEP  = 0.15;
const START = 1.05;
const MIN   = 0.0;
const MAX   = 2.4;

const ContrastGame = () => {
  useEffect(() => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
}, []);
  const [contrast,      setContrast]      = useState(START);
  const [letters,       setLetters]       = useState([]);
  const [userInput,     setUserInput]     = useState("");
  const [startTime,     setStartTime]     = useState(null);
  const [results,       setResults]       = useState([]);
  const [page,          setPage]          = useState("game");
  const [finalContrast, setFinalContrast] = useState(null);
  const [leaderboard,   setLeaderboard]   = useState([]);
  const [userRank,      setUserRank]      = useState(null);

  // ================= GENERATE LETTERS =================
  const generateLetters = () => {
    const shuffled = [...lettersSet].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  };

  // ================= LOAD NEXT =================
  const loadNext = () => {
    const newLetters = generateLetters();
    setLetters(newLetters);
    setUserInput("");
    setStartTime(performance.now());
  };

  useEffect(() => {
    loadNext();
  }, []);

  // ================= CHECK ANSWER =================
  const checkAnswer = () => {
    const end = performance.now();
    const timeTaken = ((end - startTime) / 1000).toFixed(2);

    // FIX: length guard — wrong length = incorrect
    if (userInput.trim().length !== letters.length) {
      return;
    }

    let correctCount = 0;
    for (let i = 0; i < letters.length; i++) {
      if (userInput[i]?.toUpperCase() === letters[i]) {
        correctCount++;
      }
    }

    const cleared = correctCount === letters.length;

    const entry = {
      contrast:  Number(contrast.toFixed(2)),
      time:      timeTaken,
      cleared,
      userInput,
      correct:   letters.join("")
    };

    const updatedResults = [...results, entry];
    setResults(updatedResults);

    // ❌ WRONG → END GAME IMMEDIATELY
    if (!cleared) {
      // FIX: pass current contrast explicitly, don't look up from stale state
      const finalC = Number((contrast > START ? contrast - STEP : START - STEP).toFixed(2));
      finishGame(updatedResults, finalC);
      return;
    }

    // ✅ CORRECT → INCREASE CONTRAST
    let newContrast = Number((contrast + STEP).toFixed(2));

    // STOP if max reached
    if (newContrast > MAX) {
      finishGame(updatedResults, contrast);
      return;
    }

    setContrast(newContrast);
    loadNext();
  };

  // ================= FINISH =================
  // FIX: finalC passed explicitly — no stale state lookup
  const finishGame = async (data, finalC) => {
    setPage("result");
    setFinalContrast(finalC);

    const email = localStorage.getItem("rg_email");

    try {
      await axios.post("/save-contrast", {
        email,
        finalContrast: finalC,
        sessions: data
      });
    } catch (err) {
      console.log(err);
    }
  };

  // ================= LEADERBOARD =================
  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const email = localStorage.getItem("rg_email");
        const res   = await axios.get(`/api/contrast-leaderboard?email=${email}`);

        // FIX: sort DESC — higher bestContrast = better = rank #1
        // (1.95 → Rank 1, 1.20 → Rank 2)
        const sorted = (res.data.leaderboard || [])
          .slice()
          .sort((a, b) => b.bestContrast - a.bestContrast);

        setLeaderboard(sorted);
        setUserRank(res.data.userRank);

      } catch (err) {
        console.log(err);
      }
    };

    fetchLeaderboard();
    const interval = setInterval(fetchLeaderboard, 2000);
    return () => clearInterval(interval);
  }, []);

  // ================= SHADE LOGIC — PYTHON MATCH =================
  // Python:
  //   n          = contrast / 0.15
  //   blackShade = 0.59 ^ n        (exponential decay)
  //   shade      = 1 - blackShade  (~0.983 at contrast 1.05 → very light grey)
  //   rgb        = shade * 255     → MUST be on WHITE background
  //
  // contrast=1.05 → rgb(250,250,250) on white = barely visible ✓
  // contrast=2.40 → rgb(255,255,255) on white = invisible ✓
  const getShadeColor = (contrastValue) => {
    const n          = contrastValue / 0.15;
    const blackShade = Math.pow(0.59, n);
    const shade      = 1 - blackShade;
    const rgb        = Math.round(shade * 255);
    return `rgb(${rgb}, ${rgb}, ${rgb})`;
  };

  // ================= RESTART =================
  const restartGame = () => {
    setContrast(START);
    setResults([]);
    setFinalContrast(null);
    setPage("game");
    loadNext();
  };

  // ================= UI =================
  return (
    <div className="contrast-game-container">

      {/* ================= GAME PAGE ================= */}
      {page === "game" && (
        <>
          <h2>Contrast Level: {contrast}</h2>

          {/* letters-box background MUST be white in CSS for shade to work */}
          <div className="letters-box">
            {letters.map((l, i) => (
              <span
                key={i}
                className="letter"
                style={{ color: getShadeColor(contrast) }}
              >
                {l}
              </span>
            ))}
          </div>

          <input
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            maxLength={3}
            placeholder="Enter letters"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") checkAnswer();
            }}
          />

          <button onClick={checkAnswer}>Submit</button>
        </>
      )}

    {/* ================= RESULT PAGE ================= */}
    {page === "result" && (

      <div className="result-big-container">

        {/* LEADERBOARD */}
        <div className="leaderboard-box">

          <h3>Leaderboard</h3>

          <table className="leaderboard-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Name</th>
                <th>Best Contrast</th>
                <th>Medal</th>
              </tr>
            </thead>
            

            <tbody>
             {leaderboard.slice(0, 5).map((player, index) => {

              let medal = "";

              if (player.rank === 1) {
                medal = "🥇";
              } else if (player.rank === 2) {
                medal = "🥈";
              } else if (player.rank === 3) {
                medal = "🥉";
              }

              return (
                <tr key={index}>
                  <td>{player.rank}</td>
                  <td>{player.name}</td>
                  <td>{player.bestContrast}</td>
                  <td>{medal}</td>
                </tr>
              );

            })}
            </tbody>
          </table>

          {userRank && (
            <div className="your-rank-box">
              <p><b>Your Rank:</b> {userRank.rank}</p>
              <p><b>Your Contrast:</b> {finalContrast}</p>
              
            </div>
          )}

        </div>

        {/* USER RESULTS */}
        <div className="results-box">

          <h2>Your Attempts</h2>

          <ul>
            {results.map((r, i) => (
              <li key={i}>
                Attempt {i+1}:
                Contrast {r.contrast} |
                Time {r.time}s |
                {r.cleared ? " ✅ Correct" : " ❌ Wrong"}
              </li>
            ))}
          </ul>

          <button onClick={restartGame} className="try-again-buttonr">
            Try Again
          </button>

          <button 
            onClick={() => window.location.href="/dashboard-login"} 
            className="exit-buttonr"
          >
            Exit
          </button>

        </div>

      </div>

    )}

  </div>
);
  
};

export default ContrastGame;